/**
 * 金額の表示。API の金額はすべて**最小通貨単位**（usd = セント、jpy = 円）の整数で来る（Stripe と同じ）。
 *
 * WHY Intl から桁数を引くか: 通貨ごとの小数桁（usd 2 / jpy 0 / kwd 3）を表で持つと必ず漏れる。
 * ブラウザの Intl が ISO 4217 の桁数を知っているので、それに従う（サーバーの formatPrice と同じ考え方）。
 * Stripe の「ゼロ小数通貨」と Intl の桁数が食い違う通貨（例: isk・huf の一部表示）は将来サーバーから
 * 桁数を返してもらう。今のところ config の商品で使われる主要通貨では一致している。
 */

import { formatPercent, intlLocale } from './i18n';

const digitsCache = new Map<string, number>();

/** その通貨の小数桁（usd → 2、jpy → 0）。未知の通貨コードは 2 とみなす */
export function minorDigits(currency: string): number {
  const c = currency.toUpperCase();
  let d = digitsCache.get(c);
  if (d === undefined) {
    try {
      // 桁数は ISO 4217 で決まり言語に依らないので、表示中の locale で引いてよい（キャッシュは通貨だけで持つ）
      d = new Intl.NumberFormat(intlLocale(), { style: 'currency', currency: c }).resolvedOptions().maximumFractionDigits ?? 2;
    } catch {
      d = 2;
    }
    digitsCache.set(c, d);
  }
  return d;
}

export const toMajor = (minor: number, currency: string) => minor / 10 ** minorDigits(currency);

/** 4.99 → "$4.99"（ja: USD は "$4.99"、JPY は "￥500"）。compact = グラフの目盛り用（"$1.2K"）。書式は表示中の locale */
export function money(minor: number, currency: string, { compact = false }: { compact?: boolean } = {}): string {
  const c = currency.toUpperCase();
  try {
    return new Intl.NumberFormat(intlLocale(), {
      style: 'currency',
      currency: c,
      ...(compact ? { notation: 'compact', maximumFractionDigits: 1 } : {})
    }).format(toMajor(minor, currency));
  } catch {
    return `${toMajor(minor, currency).toFixed(2)} ${c}`;
  }
}

/** 手数料 bps → "10%"（1000 bps = 10%） */
export const percentFromBps = (bps: number) => formatPercent(bps / 10_000);

/** toMajor の逆（グラフは主単位で描くので、吹き出しの書式で最小単位へ戻す）。丸めは浮動小数の誤差（4.99 × 100）対策 */
export const fromMajor = (major: number, currency: string) => Math.round(major * 10 ** minorDigits(currency));
